import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import CryptoJS from "crypto-js";
import usePostMessage from "../hooks/post-message";
import { MbMember } from "src/api/member/member-types";
import { useNavigateGoBack } from "../hooks/navigate-go-back";

// SSO 페이로드 타입
interface SSOPayload {
  nonce: string;
  email: string;
  external_id: string;
  username?: string;
  name?: string;
  avatar_url?: string;
  bio?: string;
  admin?: boolean;
  moderator?: boolean;
  suppress_welcome_message?: boolean;
}

// Discourse SSO 시크릿 키
const DISCOURSE_SECRET = process.env.REACT_APP_DISCOURSE_SECRET || "";

const DiscourseSSO: React.FC = () => {
  /**
   * useLocation
   */
  const location = useLocation();

  /**
   * Ref
   */
  const nonceRef = useRef<string | undefined>(undefined);
  const returnSsoUrlRef = useRef<string | undefined>(undefined);
  const initializedRef = useRef<boolean>(false);

  /**
   * usePostMessage
   */
  // 회원정보 조회 요청
  const { postMessageGetMember } = usePostMessage();
  const { postMessageGoBackNavigation } = usePostMessage();
  const { goBack } = useNavigateGoBack();
  /**
   * useState
   */
  const [errorType, setErrorType] = useState<null | "INVALID_ACCESS" | "SYSTEM_ERROR">(null);

  /**
   * 함수
   */
  // Discourse SSO 리다이렉트
  const redirectSSO = (member: MbMember) => {
    if (!nonceRef.current || !returnSsoUrlRef.current) {
      console.error("필수 SSO 파라미터가 없습니다.");
      setErrorType("SYSTEM_ERROR");
      return;
    }

    const emailVerified = (member.states || []).some((state) => state.status === "EMAIL_VERIFIED");

    if (!emailVerified) {
      console.error("이메일 인증이 되지 않았습니다.");
      setErrorType("SYSTEM_ERROR");
      return;
    }

    if (!member.memberNo) {
      console.error("회원 번호가 없습니다.");
      setErrorType("SYSTEM_ERROR");
      return;
    }

    const emailLocalPart = findNameByEmail(member.email);
    const nickname = member.nickname || emailLocalPart;

    // 사용자 정보 설정 (실제 구현에서는 인증된 사용자 정보를 사용해야 함)
    const ssoPayload: SSOPayload = {
      nonce: nonceRef.current,
      email: member.email,
      external_id: member.memberNo,
      username: emailLocalPart,
      name: nickname,
    };

    // 응답 페이로드 생성
    const responsePayload = new URLSearchParams(ssoPayload as any).toString();
    const base64Payload = window.btoa(responsePayload);

    // 응답 서명 생성
    const responseHmac = CryptoJS.HmacSHA256(base64Payload, DISCOURSE_SECRET);
    const responseSig = responseHmac.toString(CryptoJS.enc.Hex);

    // Discourse로 리다이렉트
    const redirectUrl = `${returnSsoUrlRef.current}?sso=${encodeURIComponent(base64Payload)}&sig=${responseSig}`;

    // setTimeout(() => {
    window.location.replace(redirectUrl);
    // }, 1000);
  };

  // 회원정보 콜백
  const memberInfoCallback = (memberInfo?: string) => {
    if (!memberInfo) {
      console.error("memberInfo is not found");
      setErrorType("SYSTEM_ERROR");
      return;
    }

    if (!initializedRef.current) {
      initializedRef.current = true;
    }

    const member: MbMember = JSON.parse(memberInfo);
    console.debug("member", member);

    redirectSSO(member);
  };

  // SSO 처리
  const handleSSO = () => {
    if (!window.ReactNativeWebView) {
      setErrorType("INVALID_ACCESS");
      return;
    }

    const params = new URLSearchParams(location.search);
    const sso = params.get("sso");
    const sig = params.get("sig");
    console.debug("sso", sso);
    console.debug("sig", sig);

    if (!sso || !sig) {
      console.error("SSO 파라미터가 없습니다.");
      setErrorType("SYSTEM_ERROR");
      return;
    }

    // 서명 검증
    const hmac = CryptoJS.HmacSHA256(sso, DISCOURSE_SECRET);
    const expectedSig = hmac.toString(CryptoJS.enc.Hex);
    console.log("expectedSig", expectedSig);

    if (sig !== expectedSig) {
      console.error("잘못된 서명입니다.");
      setErrorType("SYSTEM_ERROR");
      return;
    }

    // SSO 페이로드 디코딩
    const decodedPayload = window.atob(sso);
    const payload = new URLSearchParams(decodedPayload);
    nonceRef.current = payload.get("nonce") || undefined;
    returnSsoUrlRef.current = payload.get("return_sso_url") || undefined;

    console.debug("nonceRef.current", nonceRef.current);
    console.debug("returnSsoUrlRef.current", returnSsoUrlRef.current);

    if (!nonceRef.current || !returnSsoUrlRef.current) {
      console.error("필수 SSO 파라미터가 없습니다.");
      setErrorType("SYSTEM_ERROR");
      return;
    }

    postMessageGetMember();
  };

  // 이메일 기반 이름 구하기
  const findNameByEmail = (email?: string) => {
    if (!email) {
      return email;
    }

    const atIndex = email.indexOf("@");
    if (atIndex < 0) {
      return email;
    }

    return email.substring(0, atIndex);
  };

  /**
   * useEffect
   */
  useEffect(() => {
    window.court.webViewFunction.onMember = memberInfoCallback;
    handleSSO();
  }, []);

  return (
    <>
      {errorType ? (
        <div className="error-page">
          <section>
            <div className="error-icon ic-exclamation"></div>
            <div className="error-message-wrap">
              {errorType === "INVALID_ACCESS" ? (
                <>
                  <h2>올바르지 않은 접근입니다.</h2>
                </>
              ) : errorType === "SYSTEM_ERROR" ? (
                <>
                  <h2>오류가 발생했습니다.</h2>
                  <p>잠시 후 다시 시도해주세요.</p>
                </>
              ) : null}
            </div>
          </section>
          <div className="base-absolute-btn-wrap">
            <button type="button" className="base-btn w-100" onClick={() => (window.ReactNativeWebView ? postMessageGoBackNavigation() : goBack())}>
              돌아가기
            </button>
          </div>
        </div>
      ) : (
        <div className="error-page">
          <section className="connect">
            <p>커뮤니티에 연결 중입니다.</p>
            <button onClick={() => (window.ReactNativeWebView ? postMessageGoBackNavigation() : goBack())}>돌아가기</button>
          </section>
        </div>
      )}
    </>
  );
};

export default DiscourseSSO;
